import { DataGrid } from '@tableau/data-grid-1-wrapper';
import { DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import * as React from 'react';
import { ServiceLocator } from '../../../app/ServiceLocator';
import { Formatters, Messages } from '../../../codegen/Localize';
import { DataGridCells } from '../../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../../common/react/components/dataGrid/SortableHeader';
import { ErrorDetailsDialog, ErrorDetailsDialogProps } from '../../../common/react/components/ErrorDetailsDialog';
import { imgUrl } from '../../../common/utilities/ImgUrl';
import { IUserInfo } from '../../../ts/models/IUserInfo';
import { Set } from '../../../ts/models/Set';
import { ModalInstance, ReactModalService } from '../../../commonInfra/dialogs/ReactModalService';
import { SliceableHelper } from '../../../ts/helpers/SliceableHelper';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';

export enum DetailType { Success, Error }
export interface ISiteMembershipDetail{
    userName: string;
    siteName: string;
    message: string;
    userInfo: IUserInfo;
    siteRole: string;
    code: number;
    detailType: DetailType;
    isUserAdded?: boolean;
}

const errorDetailsDialogColumns: DataGrid.Column[] = [
    {
        name: 'userName-col',
        dataKey: 'userName',
        headerText: 'Name',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.PlainTextCell('userName'),
        sortable: false
    },
    {
        name: 'siteName-col',
        dataKey: 'siteName',
        headerText: 'Site',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.PlainTextCell('siteName'),
        sortable: false
    },
    {
        name: 'message-col',
        dataKey: 'message',
        headerText: 'Message',
        sortable: false,
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.PlainTextCell('message'),
        cellStyle: {width: 'auto'}, // TODO: TFS 769624 - Use StyledCellFormatter instead of cellStyle
        resizable: false
    }
];

const arrowUpStyle: React.CSSProperties = {
    position: 'absolute',
    width: '30px',
    height: '16px',
    top: '-15px',
    background: imgUrl('modal_arrow_up.svg') + ' no-repeat center'
};

export const ERROR_DETAILS_DIALOG_PADDING_SIZE = 30;
const dialogBodyStyle: React.CSSProperties = {
    padding: ERROR_DETAILS_DIALOG_PADDING_SIZE.toString() + 'px'
};


function notifySuccessSummary(successSummary: string){
    showDetailToast([], ToastType.Info, successSummary);
}

function notifyErrorSummary(errorSummary: string){
    showDetailToast([], ToastType.Error, errorSummary);
}

function notifySuccessResult(details: ISiteMembershipDetail[]){
    if(details.length > 0){
        const userCount = getUserCount(details);
        showDetailToast(details, ToastType.InfoNoFadeout, Messages.usersAddModifiedRemoved(
            {USER_COUNT: userCount, USER_COUNT_LOC: Formatters.formatCount(userCount)}));
    }
}

function notifyErrorResult(details: ISiteMembershipDetail[]){
    if(details.length>0){
        const userCount = getUserCount(details);
        showDetailToast(details, ToastType.Error, Messages.usersNotAddModifiedRemoved(
            {USER_COUNT: userCount, USER_COUNT_LOC: Formatters.formatCount(userCount)}));
    }
}

function getUserCount(details: ISiteMembershipDetail[]): number{
    const userSet = new Set<string>(Set.Identity);
    for(const detail of details){
        userSet.add(detail.userInfo.id);
    }
    return userSet.size();
}

function showDetailToast(details: ISiteMembershipDetail[], type: ToastType, summary: string) {
    ToasterService.instance.pop({
        type: type,
        text: summary,
        actionMessage: details.length > 0 ? Messages.details() : null,
        action: details.length > 0 ? () => {openDetailsModalFromToast(details);} : null,
    });
}

function openDetailsModalFromToast(errors: ISiteMembershipDetail[]) {

    const sliceable = SliceableHelper.fromArray(errors, ServiceLocator.get<ng.IQService>('$q'));
    let modalInstance: ModalInstance;

    const dialogProps: ErrorDetailsDialogProps = {
        close: (result: any) => modalInstance && modalInstance.close(result),
        sliceable: sliceable,
        columns: errorDetailsDialogColumns,
        hideArrow: true,
    };

    modalInstance = ReactModalService.open({
        type: DialogType.NormalWithTitleBar,
        title: Messages.siteMembership(),
        dialogContent: <ErrorDetailsDialog {...dialogProps}/>,
        testId: 'errorDetailsDialog',
        showCloseButton: true,
        shouldNotSetDefaultWidth: true,
        positioningBehavior: DialogPositioningBehavior.Centered,
        dialogBodyStyle: dialogBodyStyle
    });
}

export const MultipleSiteRoleMembershipActionNotification = {
    notifySuccessSummary,
    notifySuccessResult,
    notifyErrorSummary,
    notifyErrorResult,
    openDetailsModalFromToast
};
